// Wave 20.16 — Cookie consent banner.
//
// GDPR/ePrivacy-friendly: no non-essential cookies fire until the user
// explicitly opts in. Reject is exactly as prominent as Accept (no dark
// patterns). Preferences persist to localStorage and survive across
// visits.
//
// Storage key: aero_cookie_consent
// Value (JSON):
//   { version: 1, decidedAt: ISO string, choices: { analytics, preferences } }
// A stored version < CONSENT_VERSION reopens the banner (used when we
// materially change what we collect).
//
// Any component may reopen the customize modal with:
//   window.dispatchEvent(new Event('aero:cookies:reopen'))

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Paper,
  Slide,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { colors } from '../theme';

const STORAGE_KEY = 'aero_cookie_consent';
const CONSENT_VERSION = 1;

type Choices = { analytics: boolean; preferences: boolean };

interface Consent {
  version: number;
  decidedAt: string;
  choices: Choices;
}

function readConsent(): Consent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Consent;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(choices: Choices) {
  const record: Consent = {
    version: CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
    choices,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent('aero:cookies:changed', { detail: record }));
}

export default function CookieBanner() {
  const [decided, setDecided] = useState<boolean>(() => readConsent() !== null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [draft, setDraft] = useState<Choices>({ analytics: false, preferences: false });

  useEffect(() => {
    const reopen = () => {
      const existing = readConsent();
      setDraft(existing?.choices ?? { analytics: false, preferences: false });
      setCustomizeOpen(true);
    };
    window.addEventListener('aero:cookies:reopen', reopen);
    return () => window.removeEventListener('aero:cookies:reopen', reopen);
  }, []);

  const acceptAll = () => {
    writeConsent({ analytics: true, preferences: true });
    setDecided(true);
    setCustomizeOpen(false);
  };

  const rejectNonEssential = () => {
    writeConsent({ analytics: false, preferences: false });
    setDecided(true);
    setCustomizeOpen(false);
  };

  const saveCustom = () => {
    writeConsent(draft);
    setDecided(true);
    setCustomizeOpen(false);
  };

  const openCustomize = () => {
    const existing = readConsent();
    setDraft(existing?.choices ?? { analytics: false, preferences: false });
    setCustomizeOpen(true);
  };

  const showBanner = !decided && !customizeOpen;

  return (
    <>
      <Slide in={showBanner} direction="up" mountOnEnter unmountOnExit>
        <Paper
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          elevation={12}
          sx={{
            position: 'fixed',
            zIndex: (t) => t.zIndex.snackbar,
            left: { xs: 12, md: 24 },
            right: { xs: 12, md: 24 },
            bottom: { xs: 12, md: 24 },
            p: { xs: 2, md: 3 },
            backgroundColor: colors.surfaceElevated,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 3 }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: colors.text, mb: 0.5 }}>
                We use cookies
              </Typography>
              <Typography sx={{ color: colors.textMuted, fontSize: '0.9rem', lineHeight: 1.55 }}>
                Strictly necessary cookies keep you signed in and let the site work. With your
                consent we also use analytics cookies to understand how users use the product
                and preference cookies to remember choices like your language.{' '}
                <Link
                  component={RouterLink}
                  to="/about"
                  sx={{ color: colors.brand, textDecorationColor: colors.brand }}
                >
                  Learn more
                </Link>
              </Typography>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ flexShrink: 0 }}
            >
              <Button
                variant="outlined"
                onClick={rejectNonEssential}
                sx={{
                  color: colors.text,
                  borderColor: colors.border,
                  '&:hover': { borderColor: colors.textMuted, backgroundColor: 'transparent' },
                }}
              >
                Reject non-essential
              </Button>
              <Button
                variant="text"
                onClick={openCustomize}
                sx={{ color: colors.textMuted }}
              >
                Customize
              </Button>
              <Button
                variant="contained"
                onClick={acceptAll}
                sx={{
                  backgroundColor: colors.brand,
                  color: colors.textInverse,
                  fontWeight: 700,
                  '&:hover': { backgroundColor: colors.brandBright },
                }}
              >
                Accept all
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Slide>

      <Dialog
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: { backgroundColor: colors.surfaceElevated, border: `1px solid ${colors.border}` },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Cookie preferences</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.textMuted, fontSize: '0.9rem', mb: 2 }}>
            Choose which categories of cookies aero.chat can use on this device. You can
            change this at any time from your Account page.
          </Typography>

          <CategoryRow
            title="Strictly necessary"
            body="Required for authentication, session state, and security. These are always on because the site cannot function without them."
            checked
            disabled
          />
          <Divider sx={{ my: 1.5, borderColor: colors.border }} />
          <CategoryRow
            title="Analytics"
            body="Aggregated usage data (page views, feature usage, error reports) so we can improve the product."
            checked={draft.analytics}
            onChange={(v) => setDraft((d) => ({ ...d, analytics: v }))}
          />
          <Divider sx={{ my: 1.5, borderColor: colors.border }} />
          <CategoryRow
            title="Preferences"
            body="Remembers non-essential choices like your language and preferred units."
            checked={draft.preferences}
            onChange={(v) => setDraft((d) => ({ ...d, preferences: v }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={rejectNonEssential} sx={{ color: colors.textMuted }}>
            Reject non-essential
          </Button>
          <Button
            variant="contained"
            onClick={saveCustom}
            sx={{
              backgroundColor: colors.brand,
              color: colors.textInverse,
              '&:hover': { backgroundColor: colors.brandBright },
            }}
          >
            Save preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function CategoryRow({
  title,
  body,
  checked,
  disabled = false,
  onChange,
}: {
  title: string;
  body: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 0.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: colors.text }}>{title}</Typography>
        <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mt: 0.25 }}>
          {body}
        </Typography>
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(_, v) => onChange?.(v)}
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': { color: colors.brand },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.brand },
        }}
      />
    </Stack>
  );
}

// Helper for other components to read the current consent state.
export function getConsent(): Consent | null {
  return readConsent();
}
